import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import type { SessionUser } from "@gsp/shared";
import { api } from "./api";

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  loginDemo: (roleKey: "supplier" | "planner" | "compliance" | "admin") => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginEntra: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.get<{ user: SessionUser | null }>("/auth/me");
      setUser(user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginDemo = useCallback(async (roleKey: "supplier" | "planner" | "compliance" | "admin") => {
    const { user } = await api.post<{ user: SessionUser }>("/auth/demo-login", { roleKey });
    setUser(user);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { user } = await api.post<{ user: SessionUser }>("/auth/login", { email, password });
    setUser(user);
  }, []);

  const loginEntra = useCallback(async () => {
    const { user } = await api.post<{ user: SessionUser }>("/auth/entra-login");
    setUser(user);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setUser(null);
  }, []);

  return <AuthContext.Provider value={{ user, loading, loginDemo, login, loginEntra, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
